import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';
import { GlossaryTerm } from '@/components/glossary/GlossaryTerm';
import { SearchIcon } from '@/components/ui/icons';
import glossaryData from '@/data/glossary.json';

export function GlossairePage() {
  const { t } = useTranslation('glossaire');
  const { locale } = useParams();
  const currentLocale = locale || 'fr';

  const [searchTerm, setSearchTerm] = useState('');

  // Filtrer les termes
  const filteredTerms = useMemo(() => {
    let filtered = glossaryData;

    // Filtre par recherche textuelle
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (term) =>
          term.term.toLowerCase().includes(search) ||
          term.definition.toLowerCase().includes(search) ||
          term.synonyms?.some((syn) => syn.toLowerCase().includes(search))
      );
    }

    // Trier par ordre alphabétique
    return filtered.sort((a, b) => a.term.localeCompare(b.term, 'fr'));
  }, [searchTerm]);

  return (
    <>
      <SEOHead
        title="Glossaire Agriculture Numérique et AgriTech | Aspexit"
        description="Glossaire complet de 30 termes techniques en agriculture numérique, AgriTech et agriculture de précision. Définitions, exemples et ressources pour maîtriser le vocabulaire du digital farming."
        path="/glossaire"
      />

      <div>
        {/* Hero */}
        <section className="bg-gradient-to-br from-aspexit-green/10 via-white to-aspexit-blue/5 py-12">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-4">
                Glossaire Agriculture Numérique
              </h1>
              <p className="text-lg text-aspexit-gray mb-6">
                {glossaryData.length} termes techniques pour maîtriser le vocabulaire de
                l'AgriTech, l'agriculture de précision et le digital farming
              </p>

              {/* Barre de recherche */}
              <div className="relative max-w-lg mx-auto">
                <SearchIcon
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Rechercher un terme, une définition..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-aspexit-green focus:border-transparent shadow-sm"
                />
              </div>
            </div>
          </div>
        </section>


        {/* Liste des termes */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            {filteredTerms.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-xl text-aspexit-gray mb-4">
                  Aucun terme trouvé
                </p>
                <p className="text-aspexit-gray mb-6">
                  Essayez de modifier vos filtres ou votre recherche
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="px-6 py-3 bg-aspexit-green text-white rounded-lg hover:bg-aspexit-green-dark transition-colors"
                >
                  Réinitialiser la recherche
                </button>
              </div>
            ) : (
              <>
                {/* Compteur de résultats */}
                <div className="mb-6 text-center">
                  <p className="text-sm text-aspexit-gray">
                    {filteredTerms.length} terme{filteredTerms.length > 1 ? 's' : ''}{' '}
                    {searchTerm && `pour "${searchTerm}"`}
                  </p>
                </div>

                {/* Grille de termes */}
                <div className="space-y-6">
                  {filteredTerms.map((term) => (
                    <GlossaryTerm key={term.id} {...term} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* CTA Bottom */}
        <section className="py-16 bg-gradient-to-r from-aspexit-green to-aspexit-green-dark text-white">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl font-heading font-bold mb-4">
              Vous voulez approfondir ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Découvrez nos formations pour maîtriser les outils de l'agriculture
              numérique
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`/${currentLocale}/formations`}
                className="px-8 py-4 bg-white text-aspexit-green rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Voir les formations
              </a>
              <a
                href={`/${currentLocale}/blog`}
                className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Lire le blog
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
